import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Minus, Plus, MoreHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getDeviceUIModel } from '../../lib/deviceUiModel';

const DeviceTile = ({ device, onControl, onClick, loading }) => {
  const uiModel = getDeviceUIModel(device);
  const IconComponent = uiModel.icon;

  const isUnavailable = !uiModel.isAvailable;
  // A tile that is not responding never glows: it must not read as "on".
  const isLightOn = (uiModel.kind === 'light' || uiModel.kind === 'switch') && uiModel.isOn && !isUnavailable;
  // A present occupancy sensor reads as active (green) so "Present" vs "Absent"
  // is legible at a glance, not just in the label text.
  const isPresenceActive = uiModel.kind === 'sensor' && uiModel.isOn;

  const iconWrapClass = cn(
    "relative rounded-full flex items-center justify-center shrink-0 border",
    uiModel.tileSize === "large" ? "w-8 h-8" : "w-7 h-7",
    isLightOn
      ? "bg-amber-100 text-amber-700 border-amber-200/60 shadow-[0_6px_18px_rgba(245,158,11,0.25)]"
      : isPresenceActive
        ? "bg-green-100 text-green-700 border-green-200/60 dark:bg-green-500/15 dark:text-green-400 dark:border-green-400/25"
        : "bg-muted text-muted-foreground border-muted-foreground/10"
  );

  const handleSwitchChange = (value) => {
    if (onControl) {
      const binaryControl = device.controls?.find(c => c.valueType === 'binary');
      if (binaryControl) {
        const toggleValue = value
          ? binaryControl.options?.on || true
          : binaryControl.options?.off || false;
        onControl(device.id, binaryControl.property, toggleValue, device.parentId);
      }
    }
  };

  const handleThermostatAdjust = (direction) => {
    if (!onControl) return;
    const setpoint = device.controls?.find(c =>
      c.property?.includes('current_heating_setpoint')
    );
    if (!setpoint) return;
    // Range lives under options.{min,max,step}; value_* is a legacy fallback.
    const step = setpoint.options?.step ?? setpoint.value_step ?? 1;
    const min = setpoint.options?.min ?? setpoint.value_min ?? 5;
    const max = setpoint.options?.max ?? setpoint.value_max ?? 35;
    const newValue = Math.max(min, Math.min(max, setpoint.value + direction * step));
    onControl(device.id, setpoint.property, newValue, device.parentId);
  };

  const handleCurtainAction = (action) => {
    if (onControl) {
      onControl(device.id, 'state', action, device.parentId);
    }
  };

  const stopTilePropagation = {
    onClick: (e) => e.stopPropagation(),
    onPointerDown: (e) => e.stopPropagation(),
  };

  const isLargeTile = uiModel.tileSize === 'large';
  const isToggleTile = uiModel.primaryControlType === 'switch';

  // On mobile a tap toggles the tile, so details open via long-press (touch
  // only). Desktop gets the hover "⋯" button instead, so long-press stays out
  // of the mouse's way.
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const startLongPress = (e) => {
    if (!isToggleTile || e.pointerType !== 'touch') return;
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onClick?.();
    }, 500);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Don't let a pending long-press fire after the tile unmounts (device removed,
  // filtered out, or list refreshed mid-hold).
  useEffect(() => cancelLongPress, []);

  const activateTile = () => {
    // A long-press already opened details — swallow the trailing click.
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    if (isToggleTile) {
      if (!loading) handleSwitchChange(!uiModel.isOn);
    } else {
      onClick?.();
    }
  };

  const handleTileKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activateTile();
    }
  };

  const renderPrimaryControl = () => {
    switch (uiModel.primaryControlType) {
      case 'switch':
        return (
          <Switch
            checked={uiModel.isOn}
            onCheckedChange={handleSwitchChange}
            disabled={loading}
          />
        );

      case 'thermostat': {
        const setpoint = device.controls?.find(c =>
          c.property?.includes('current_heating_setpoint')
        );
        // Room temperature reading lives in sensors (local_temperature), not
        // controls; the controls lookup is a fallback for devices that model
        // it differently.
        const currentTemp = device.sensors?.find(s =>
          s.property?.includes('local_temperature') ||
          s.property?.includes('device_temperature')
        )?.value ?? device.controls?.find(c => c.property === 'temperature')?.value;
        const hasCurrent = currentTemp !== null && currentTemp !== undefined;
        const target = setpoint?.value;

        return (
          <div className="flex items-end justify-between gap-2">
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className="text-2xl font-semibold">
                {hasCurrent
                  ? `${Math.round(currentTemp)}°`
                  : target !== undefined
                    ? `${Math.round(target)}°`
                    : '--°'}
              </span>
              {hasCurrent && setpoint && (
                <span className="text-xs text-muted-foreground/70">
                  Target {Math.round(target)}°
                </span>
              )}
            </div>
            {setpoint && (
              <div className="flex items-center gap-1.5 shrink-0" {...stopTilePropagation}>
                <button
                  type="button"
                  aria-label="Decrease target temperature"
                  disabled={loading}
                  onClick={() => handleThermostatAdjust(-1)}
                  className="w-10 h-10 rounded-full border border-border/60 bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-50"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  aria-label="Increase target temperature"
                  disabled={loading}
                  onClick={() => handleThermostatAdjust(1)}
                  className="w-10 h-10 rounded-full border border-border/60 bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        );
      }

      case 'curtain': {
        const positionControl = device.controls?.find(c => c.property === 'position');
        const position = positionControl?.value;
        const curtainStateControl = device.controls?.find(c =>
          c.property === 'state' &&
          c.type === 'enum' &&
          Array.isArray(c.options?.values)
        );

        let stateText = '';

        if (position !== null && position !== undefined) {
          if (position >= 98) {
            stateText = 'Open';
          } else if (position <= 2) {
            stateText = 'Closed';
          } else {
            stateText = `Open ${Math.round(position)}%`;
          }
        } else if (curtainStateControl?.value) {
          const value = curtainStateControl.value;
          if (value === 'OPEN') {
            stateText = 'Open';
          } else if (value === 'CLOSE') {
            stateText = 'Closed';
          } else if (value === 'STOP') {
            stateText = 'Stopped';
          } else {
            stateText = value;
          }
        }

        return (
          <div className="space-y-2">
            <div className="text-center">
              <span className="text-sm font-medium">{stateText}</span>
            </div>
            <div className="flex gap-1.5" {...stopTilePropagation}>
              <Button
                variant="outline"
                size="sm"
                disabled={loading}
                onClick={() => handleCurtainAction('OPEN')}
                className="flex-1 h-10 text-xs"
              >
                Open
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={loading}
                onClick={() => handleCurtainAction('STOP')}
                className="flex-1 h-10 text-xs"
              >
                Stop
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={loading}
                onClick={() => handleCurtainAction('CLOSE')}
                className="flex-1 h-10 text-xs"
              >
                Close
              </Button>
            </div>
          </div>
        );
      }

      case 'sensor':
        return (
          <div className="text-center">
            <span className="text-sm font-medium">{uiModel.primaryLabel}</span>
          </div>
        );

      case 'lock':
        return (
          <div className="text-center">
            <span className="text-sm font-medium">{uiModel.primaryLabel}</span>
          </div>
        );

      default:
        return null;
    }
  };

  const tileClass = cn(
    "relative overflow-hidden transition-all duration-200",
    "hover:scale-[0.98] active:scale-[0.96]",
    "cursor-pointer group",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
    isLargeTile ? "col-span-2" : "col-span-1 min-h-[88px]",
    isToggleTile && isLightOn && "border-amber-200/70 dark:border-amber-400/25",
    isUnavailable && "opacity-60"
  );

  return (
    <Card
      className={tileClass}
      onClick={activateTile}
      onKeyDown={!isLargeTile ? handleTileKeyDown : undefined}
      onPointerDown={startLongPress}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onContextMenu={isToggleTile ? (e) => { e.preventDefault(); onClick?.(); } : undefined}
      tabIndex={isLargeTile ? undefined : 0}
      role={isLargeTile ? undefined : (isToggleTile ? 'switch' : 'button')}
      aria-checked={isToggleTile ? uiModel.isOn : undefined}
      aria-label={isLargeTile ? undefined : `${device.name || device.friendly_name || 'Device'}${isUnavailable ? ', no response' : isToggleTile ? (uiModel.isOn ? ', on' : ', off') : ''}`}
    >
      {isToggleTile ? (
        <div className={cn(
          "absolute inset-0 transition-opacity duration-200 bg-gradient-to-br",
          isLightOn
            ? "opacity-100 from-amber-200/50 via-amber-100/15 to-transparent dark:from-amber-400/20 dark:via-amber-400/[0.08]"
            : "opacity-30 from-primary/10 to-transparent"
        )} />
      ) : (
        <div className="absolute inset-0 opacity-30 bg-gradient-to-br from-primary/20 via-transparent to-primary/5" />
      )}

      <div className="relative p-2.5 h-full">
        {isLargeTile ? (
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <motion.div
                  className={iconWrapClass}
                  initial={false}
                  animate={{
                    scale: isLightOn ? 1 : 0.98,
                    opacity: isLightOn ? 1 : 0.85,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 420,
                    damping: 35,
                    mass: 0.6,
                  }}
                >
                  {isLightOn && (
                    <motion.div
                      className="absolute -inset-2 rounded-full bg-amber-300/30 blur-md"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    />
                  )}
                  <IconComponent className={cn(
                    uiModel.tileSize === "large" ? "w-4 h-4" : "w-3.5 h-3.5",
                    "relative z-10",
                  )} />
                </motion.div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-[13px] font-medium leading-tight" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {device.name || device.friendly_name || 'Unnamed Device'}
                  </h3>
                  <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                    {uiModel.secondaryLabel}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-auto">
              {renderPrimaryControl()}
            </div>
          </div>
        ) : isToggleTile ? (
          // Whole-tile toggle: the card is the switch. Tap toggles; state reads
          // from the fill + icon, not a control. Name gets the full width.
          <div className="flex flex-col justify-between h-full">
            <div className="flex items-start justify-between">
              <motion.div
                className={cn(iconWrapClass, "shrink-0")}
                initial={false}
                animate={{ scale: isLightOn ? 1 : 0.97, opacity: isLightOn ? 1 : 0.85 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              >
                {isLightOn && (
                  <motion.div
                    className="absolute -inset-2 rounded-full bg-amber-300/30 blur-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
                <IconComponent className="w-3.5 h-3.5 relative z-10" />
              </motion.div>

              {/* Details: desktop-only affordance. Mobile uses long-press. */}
              <button
                type="button"
                aria-label="Device settings"
                onClick={(e) => { e.stopPropagation(); onClick?.(); }}
                onPointerDown={(e) => e.stopPropagation()}
                className="hidden md:flex shrink-0 -mt-0.5 -mr-0.5 h-7 w-7 rounded-full items-center justify-center text-muted-foreground/40 hover:text-foreground hover:bg-background/50 opacity-60 group-hover:opacity-100 transition-all"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>

            <div className="min-w-0 mt-2">
              <p className="text-[13px] font-medium leading-tight" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {device.name || device.friendly_name || 'Unnamed Device'}
              </p>
              <p className={cn(
                "text-[11px] font-medium mt-0.5",
                isUnavailable ? "text-muted-foreground" : "text-muted-foreground/70"
              )}>
                {isUnavailable ? 'No Response' : uiModel.isOn ? 'On' : 'Off'}
              </p>
            </div>
          </div>
        ) : (
          // Non-actuatable small tile (sensor / unknown): tap opens details.
          <div className="flex items-center h-full">
            <div className="flex items-center w-full gap-2">
              <motion.div
                className={cn(iconWrapClass, "shrink-0")}
                initial={false}
                animate={{ scale: isLightOn ? 1 : 0.97, opacity: isLightOn ? 1 : 0.85 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              >
                <IconComponent className="w-3.5 h-3.5 relative z-10" />
              </motion.div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium leading-tight" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {device.name || device.friendly_name || 'Unnamed Device'}
                </p>
                {uiModel.primaryControlType === 'sensor' && (
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                    {uiModel.primaryLabel}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 bg-card flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        )}
      </div>
    </Card>
  );
};

export default DeviceTile;
